/**
 * Subscription Free Gift Widget — listener/observer pattern
 *
 * Does NOT intercept form submission. Instead:
 *  1. MutationObserver watches the Subscriptions app widget for plan changes.
 *  2. Disables the Add-to-Cart button via DOM when a gift is required but not chosen.
 *  3. Wraps window.fetch to detect a successful /cart/add, then adds the free
 *     gift as a separate follow-up request — fully decoupled from the form.
 *
 * Requires:
 *  - The HTML snippet (subscription-gift-widget.liquid) rendered below the
 *    Subscriptions app widget on your product page.
 *  - A Shopify Function that zeroes out any line item where the property
 *    `_subscription_free_gift` equals "true".
 */

(function () {
  'use strict';

  // ─── Config ────────────────────────────────────────────────────────────────

  const GIFT_PRODUCTS = [
    {
      id: 'barkbone',
      label: 'BarkBone Natural Instincts Peanut Butter Flavorit Nylon Dog Chew',
      needsSize: true,
      sizes: [
        { label: 'Small',  variantId: '47429262475585' },
        { label: 'Medium', variantId: '47429262410049' },
        { label: 'Large',  variantId: '47429262442817' },
      ],
    },
    {
      id: 'catnip-bubbles',
      label: 'Pet Qwerks Catnip IncrediBubbles for Cats',
      needsSize: false,
      variantId: '47819251253569',
    },
  ];

  const FREE_GIFT_PROPERTY_KEY = '_subscription_free_gift';

  /**
   * Selector for the element the Shopify Subscriptions app renders its widget
   * inside. The MutationObserver watches this subtree for DOM changes that
   * indicate a plan was selected or deselected.
   *
   * Common values depending on theme:
   *   'shopify-payment-button'         — default Subscriptions app wrapper
   *   '[data-product-form]'            — some themes wrap here
   *   'form[action="/cart/add"]'       — fallback: watch the whole form
   *
   * Check your theme's rendered HTML and update this if needed.
   */
  const SUBSCRIPTIONS_WIDGET_SELECTOR = 'shopify-payment-button, [data-subscriptions-widget], form[action="/cart/add"]';

  /**
   * How the Subscriptions app marks the active plan in its DOM.
   * It typically adds one of these to the selected option's element:
   *   - aria-checked="true"
   *   - class containing "selected" or "active"
   *   - a checked <input type="radio">
   * The detector function below checks all of these.
   */

  /** Selector for the theme's Add-to-Cart button. Adjust if needed. */
  const ATC_BUTTON_SELECTOR = '[name="add"], .product-form__submit, #AddToCart, button[data-add-to-cart]';

  // ─── State ─────────────────────────────────────────────────────────────────

  let isSubscription    = false;
  let selectedGiftId    = null;
  let selectedVariantId = null;
  let activeGiftLineKey = null; // key of the current free-gift cart line
  let giftAddInFlight   = false;

  // ─── DOM refs ──────────────────────────────────────────────────────────────

  let panel, giftReq, atcButton;

  // ─── Bootstrap ─────────────────────────────────────────────────────────────

  function init() {
    panel     = document.getElementById('gift-panel');
    giftReq   = document.getElementById('gift-required-msg');
    atcButton = document.querySelector(ATC_BUTTON_SELECTOR);

    if (!panel) {
      console.warn('[GiftWidget] #gift-panel not found.');
      return;
    }

    buildGiftCards();
    patchFetch();
    observeSubscriptionsWidget();
    observeAtcButton();
  }

  // ─── 1. MutationObserver: watch the Subscriptions widget ───────────────────
  //
  // The Subscriptions app re-renders parts of its widget when the user toggles
  // between one-time and subscribe. We watch for any attribute or child change
  // inside the widget container and re-evaluate the current purchase type.

  function observeSubscriptionsWidget() {
    const targets = [...document.querySelectorAll(SUBSCRIPTIONS_WIDGET_SELECTOR)];

    if (!targets.length) {
      console.warn('[GiftWidget] Subscriptions widget container not found. Check SUBSCRIPTIONS_WIDGET_SELECTOR.');
      return;
    }

    const observer = new MutationObserver(() => {
      const nowSub = detectSubscriptionActive();
      if (nowSub !== isSubscription) {
        setSubscriptionState(nowSub);
      }
    });

    targets.forEach((el) =>
      observer.observe(el, { attributes: true, childList: true, subtree: true })
    );

    // Also listen for native input change events as a complementary signal
    document.addEventListener('change', (e) => {
      const input = e.target;
      if (input.type === 'radio' || input.type === 'checkbox') {
        const nowSub = detectSubscriptionActive();
        if (nowSub !== isSubscription) setSubscriptionState(nowSub);
      }
    });

    // Set initial state
    setSubscriptionState(detectSubscriptionActive());
  }

  /**
   * Inspect the Subscriptions app's rendered DOM to determine whether a
   * subscription plan is currently selected.
   *
   * The app uses several patterns across themes — we check them all:
   *   a) A radio input with value != "one_time" / "" is :checked
   *   b) An element with [aria-checked="true"] whose label/value suggests a plan
   *   c) A [data-selling-plan-id] element that is marked active/selected
   *   d) A hidden input named "selling_plan" with a non-empty value
   */
  function detectSubscriptionActive() {
    // (a) Native radio — most reliable
    const radios = document.querySelectorAll(
      'input[type="radio"][name="purchase_option"], input[type="radio"][name="selling_plan_allocation"]'
    );
    for (const r of radios) {
      if (r.checked) {
        return r.value !== 'one_time' && r.value !== '' && !r.value.includes('one-time');
      }
    }

    // (b) aria-checked pattern (custom toggle buttons)
    const ariaChecked = document.querySelectorAll('[aria-checked="true"]');
    for (const el of ariaChecked) {
      const val = (el.dataset.value || el.dataset.sellingPlan || el.textContent || '').toLowerCase();
      if (val && !val.includes('one time') && !val.includes('one-time')) {
        return true;
      }
    }

    // (c) Hidden selling_plan input injected by the app when a plan is active
    const hiddenPlan = document.querySelector('input[name="selling_plan"]');
    if (hiddenPlan && hiddenPlan.value && hiddenPlan.value !== '0') {
      return true;
    }

    // (d) Subscriptions app "selected" class pattern
    const selectedPlan = document.querySelector(
      '[data-selling-plan-id].selected, [data-selling-plan-id][aria-selected="true"]'
    );
    if (selectedPlan) return true;

    return false;
  }

  // ─── 2. ATC button observer: re-acquire button if DOM updates ──────────────
  //
  // Some themes replace the ATC button after a plan is selected. We watch the
  // button's parent for replacement and re-apply our disabled state if needed.

  function observeAtcButton() {
    if (!atcButton) return;

    const parent = atcButton.parentElement;
    if (!parent) return;

    const obs = new MutationObserver(() => {
      const fresh = document.querySelector(ATC_BUTTON_SELECTOR);
      if (fresh && fresh !== atcButton) {
        atcButton = fresh;
        applyAtcButtonState();
      }
    });

    obs.observe(parent, { childList: true, subtree: true });
  }

  // ─── 3. window.fetch patch: detect cart adds, inject gift after ─────────────
  //
  // We wrap window.fetch so that when a /cart/add.js call succeeds while a
  // subscription is active and a gift is selected, we fire a follow-up request
  // to add the free gift. This never touches the form submission itself.

  function patchFetch() {
    const _fetch = window.fetch;

    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input?.url ?? '';
      const isCartAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);

      // Pass the request through untouched
      const promise = _fetch.apply(this, arguments);

      if (!isCartAdd || !isSubscription) return promise;

      // After a successful cart add, inject the free gift
      return promise.then(async (response) => {
        // Clone so the original response body can still be consumed by the theme
        const clone = response.clone();

        if (clone.ok && !giftAddInFlight) {
          try {
            const body = await clone.json();
            const addedItems = body.items ?? (body.id ? [body] : []);

            // Don't double-add if our own gift request is what triggered this
            const isSelfGift = addedItems.some(
              (item) => item.properties?.[FREE_GIFT_PROPERTY_KEY] === 'true'
            );

            if (!isSelfGift && selectedVariantId) {
              await addGiftToCart();
            }
          } catch (_) {
            // If JSON parsing fails, attempt gift add anyway
            if (selectedVariantId) await addGiftToCart();
          }
        }

        return response;
      });
    };
  }

  // ─── Gift cart operations ───────────────────────────────────────────────────

  async function addGiftToCart() {
    if (giftAddInFlight) return;
    giftAddInFlight = true;

    await removeGiftFromCart(); // enforce 1 gift per subscription

    try {
      const res = await window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(selectedVariantId, 10),
            quantity: 1,
            properties: {
              [FREE_GIFT_PROPERTY_KEY]: 'true',
              _gift_name: resolveGiftName(),
            },
          }],
        }),
      });

      if (!res.ok) throw new Error(`Gift add failed: ${res.status}`);

      const data = await res.json();
      activeGiftLineKey = data.items?.[0]?.key ?? null;

      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    } catch (err) {
      console.error('[GiftWidget] Failed to add free gift:', err);
    } finally {
      giftAddInFlight = false;
    }
  }

  async function removeGiftFromCart() {
    // Resolve the line key if we don't have it cached
    if (!activeGiftLineKey) {
      try {
        const res  = await window.fetch('/cart.js');
        const cart = await res.json();
        const line = cart.items.find(
          (i) => i.properties?.[FREE_GIFT_PROPERTY_KEY] === 'true'
        );
        if (line) activeGiftLineKey = line.key;
      } catch (_) { /* silent */ }
    }

    if (!activeGiftLineKey) return;

    try {
      await window.fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: activeGiftLineKey, quantity: 0 }),
      });
      activeGiftLineKey = null;
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    } catch (err) {
      console.error('[GiftWidget] Failed to remove free gift:', err);
    }
  }

  // ─── Gift selection (UI) ────────────────────────────────────────────────────

  function buildGiftCards() {
    const grid = document.getElementById('gift-grid');
    if (!grid) return;
    grid.innerHTML = '';

    GIFT_PRODUCTS.forEach((gift) => {
      const card = document.createElement('div');
      card.className = 'sgw-gift-card';
      card.dataset.giftId = gift.id;

      let sizeHtml = '';
      if (gift.needsSize) {
        const options = gift.sizes
          .map((s) => `<option value="${s.variantId}">${s.label}</option>`)
          .join('');
        sizeHtml = `
          <div class="sgw-size-wrap" id="size-wrap-${gift.id}">
            <label class="sgw-size-label" for="size-sel-${gift.id}">Select size</label>
            <select class="sgw-size-sel" id="size-sel-${gift.id}" data-gift-id="${gift.id}">
              <option value="">— choose size —</option>
              ${options}
            </select>
          </div>`;
      }

      card.innerHTML = `
        <span class="sgw-check-icon" aria-hidden="true"></span>
        <span class="sgw-gift-name">${gift.label}</span>
        <span class="sgw-gift-tag">Free with first box! Limit 1 free gift per order.</span>
        ${sizeHtml}
      `;

      card.addEventListener('click', (e) => {
        if (e.target.tagName === 'SELECT' || e.target.tagName === 'OPTION') return;
        selectGift(gift.id, gift.needsSize ? null : gift.variantId);
      });

      if (gift.needsSize) {
        card.querySelector(`#size-sel-${gift.id}`).addEventListener('change', function (e) {
          e.stopPropagation();
          selectGift(gift.id, this.value || null);
        });
      }

      grid.appendChild(card);
    });
  }

  function selectGift(giftId, variantId) {
    selectedGiftId    = giftId;
    selectedVariantId = variantId;

    document.querySelectorAll('.sgw-gift-card').forEach((el) => {
      const isThis = el.dataset.giftId === giftId;
      el.classList.toggle('selected', isThis);
      const sw = el.querySelector('.sgw-size-wrap');
      if (sw) sw.classList.toggle('visible', isThis);
    });

    hideGiftRequiredMsg();
    applyAtcButtonState();
  }

  // ─── Subscription state ─────────────────────────────────────────────────────

  function setSubscriptionState(active) {
    isSubscription = active;
    panel.classList.toggle('open', active);

    if (!active) {
      clearGiftSelection();
      removeGiftFromCart();
    }

    applyAtcButtonState();
  }

  // ─── ATC button gating (DOM only — no form intercept) ──────────────────────
  //
  // We disable the button when a subscription is active but no gift is chosen.
  // The Subscriptions app's own click/submit handlers are left completely alone.

  function applyAtcButtonState() {
    const btn = atcButton ?? document.querySelector(ATC_BUTTON_SELECTOR);
    if (!btn) return;

    const shouldBlock = isSubscription && !selectedVariantId;

    btn.disabled = shouldBlock;
    btn.setAttribute('aria-disabled', String(shouldBlock));

    // Show message + scroll if someone clicks while disabled
    // (handles themes that visually grey out but don't honour the disabled attr)
    btn.onclick = shouldBlock
      ? (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          showGiftRequiredMsg();
          panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      : null;
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function clearGiftSelection() {
    selectedGiftId    = null;
    selectedVariantId = null;
    document.querySelectorAll('.sgw-gift-card').forEach((el) => {
      el.classList.remove('selected');
      const sw  = el.querySelector('.sgw-size-wrap');
      if (sw) sw.classList.remove('visible');
      const sel = el.querySelector('select');
      if (sel) sel.value = '';
    });
    hideGiftRequiredMsg();
  }

  function showGiftRequiredMsg() {
    if (giftReq) giftReq.classList.add('show');
  }

  function hideGiftRequiredMsg() {
    if (giftReq) giftReq.classList.remove('show');
  }

  function resolveGiftName() {
    const gift = GIFT_PRODUCTS.find((g) => g.id === selectedGiftId);
    if (!gift) return '';
    if (!gift.needsSize) return gift.label;
    const size = gift.sizes.find((s) => s.variantId === selectedVariantId);
    return size ? `${gift.label} – ${size.label}` : gift.label;
  }

  // ─── Init ───────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
